import React, { useState, useEffect, useRef } from 'react';
import { useChatSession } from '../../hooks/useChatSession';
import { useImageMessage } from '../../hooks/useImageMessage';
import { useVoiceMessage } from '../../hooks/useVoiceMessage';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import ImageModal from '../shared/ImageModal';
import GalleryNew from './GalleryNew';
import { DiaryList } from './DiaryList';
import { DiaryEditor } from './DiaryEditor';
import VideoCallModal from './VideoCallModal';
import { FriendList } from '../friends/FriendList';
import { InviteModal } from '../friends/InviteModal';
import { Message } from '../../types';
import { extractFirstYouTubeUrl } from '../../utils/youtube';
import { extractFirstAmazonMusicUrl } from '../../utils/amazonMusic';
import type { FriendEntry } from '../../App';

interface ChatWindowProps {
  currentUserId: string;
  currentUserName: string;
  selectedFriend: FriendEntry | null;
  friends: FriendEntry[];
  onSelectFriend: (f: FriendEntry | null) => void;
  onFriendsRefresh: () => void;
  onLogout: () => void;
}

interface DiaryEntry {
  id: number;
  userId: number;
  title: string;
  body: string;
  date: string;
  imageDataUrls: string[];
  createdAtIst: string;
  updatedAtIst: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  currentUserId,
  currentUserName,
  selectedFriend,
  friends,
  onSelectFriend,
  onFriendsRefresh,
  onLogout,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const [selectedImageModal, setSelectedImageModal] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showDiaryList, setShowDiaryList] = useState(false);
  const [showDiaryEditor, setShowDiaryEditor] = useState(false);
  const [selectedDiaryEntry, setSelectedDiaryEntry] = useState<DiaryEntry | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number | null>(null);
  const initialLoadRef = useRef(true);

  const {
    messages,
    otherUser,
    hasMore,
    loadOlderMessages,
    loadMessages,
    filterMessagesByDate,
    sendMessage,
    sendTyping,
    sendRecordingStatus,
    addReaction,
    removeReaction,
    deleteMessage,
    otherUserId,
    isTyping,
    isOtherUserRecording,
    inCall,
    inVideoCall,
    callType,
    callStatus,
    isIncomingCall,
    callTimer,
    startCall,
    startVideoCall,
    endCall,
    acceptCall,
    rejectCall,
    toggleMute,
    toggleVideo,
    switchCamera,
    remoteAudioRef,
    localVideoRef,
    remoteVideoRef,
    localVideoStreamRef,
    isMuted,
    isVideoOff,
    cameraFacingMode,
  } = useChatSession(
    currentUserId,
    selectedFriend ? String(selectedFriend.id) : undefined,
    selectedFriend?.conversationId,
    selectedFriend
      ? {
          name: selectedFriend.name,
          isOnline: selectedFriend.isOnline,
          lastSeen: selectedFriend.lastSeenIst || '',
        }
      : undefined
  );

  const {
    selectedImage,
    imagePreview,
    uploading: uploadingImage,
    compressing,
    handleImageSelect,
    uploadImage,
    cancelImageUpload
  } = useImageMessage(currentUserId, otherUserId ?? '', loadMessages, replyingTo?.id);

  const {
    isRecording,
    recordingTime,
    audioBlob,
    audioUrl,
    uploading: uploadingVoice,
    startRecording,
    stopRecording,
    cancelRecording,
    uploadVoice,
    formatTime
  } = useVoiceMessage(currentUserId, otherUserId ?? '', loadMessages, replyingTo?.id);

  const uploading = uploadingImage || uploadingVoice;

  // Send recording status to other user when recording state changes
  useEffect(() => {
    if (sendRecordingStatus) {
      sendRecordingStatus(isRecording);
    }
  }, [isRecording, sendRecordingStatus]);

  // Handle typing status with debounce
  useEffect(() => {
    if (!sendTyping) return;

    const isCurrentlyTyping = inputMessage.trim().length > 0;
    
    // Send typing status immediately when user starts typing
    if (isCurrentlyTyping) {
      sendTyping(true);
      
      // Set up a debounce to stop typing status after 3 seconds of no typing
      const typingTimeout = setTimeout(() => {
        sendTyping(false);
      }, 3000);

      return () => {
        clearTimeout(typingTimeout);
      };
    } else {
      // If input is cleared, stop typing immediately
      sendTyping(false);
    }
  }, [inputMessage, sendTyping]);

  // Stop typing status when message is sent
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    if (sendTyping) {
      sendTyping(false);
    }
    const replyId = replyingTo?.id;
    const messageToSend = inputMessage; // Store the message before clearing
    const youtubeUrl = extractFirstYouTubeUrl(inputMessage);
    const amazonMusicUrl = extractFirstAmazonMusicUrl(inputMessage);
    
    let success = false;
    if (youtubeUrl) {
      success = await sendMessage('YouTube', youtubeUrl, replyId);
    } else if (amazonMusicUrl) {
      success = await sendMessage('AmazonMusic', amazonMusicUrl, replyId);
    } else {
      success = await sendMessage('Text', messageToSend, replyId);
    }
    
    // Only clear the input message if the send was successful
    if (success) {
      setInputMessage('');
      setReplyingTo(null);
    } else {
      // Show a user-friendly error message without clearing the input
      console.warn('Message could not be sent. Please check your connection and try again.');
    }
  };

  const handleImageUpload = async () => {
    await uploadImage();
    setReplyingTo(null);
  };

  const handleVoiceUpload = async () => {
    await uploadVoice();
    setReplyingTo(null);
  };

  const handleReplyToMessage = (message: Message) => {
    setReplyingTo(message);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  useEffect(() => {
    if (messages.length === 0) return;
    
    const lastMessage = messages[messages.length - 1];
    const lastMessageId = lastMessage?.id;
    
    if (initialLoadRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
      initialLoadRef.current = false;
      lastMessageIdRef.current = lastMessageId;
    } else if (lastMessageId && lastMessageId !== lastMessageIdRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      lastMessageIdRef.current = lastMessageId;
    }
  }, [messages]);

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    
    setLoadingMore(true);
    try {
      const oldestMessage = messages[0];
      await loadOlderMessages(oldestMessage.id);
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  };


  const handleVideoCall = () => {
    if (headerUser?.isOnline) startVideoCall();
  };

  const handleAudioCall = () => {
    if (headerUser?.isOnline) startCall();
  };

  const handleDiarySelect = (entry: DiaryEntry) => {
    setSelectedDiaryEntry(entry);
    setShowDiaryList(false);
    setShowDiaryEditor(true);
  };

  const handleNewDiaryEntry = () => {
    setSelectedDiaryEntry(null);
    setShowDiaryList(false);
    setShowDiaryEditor(true);
  };

  const handleDiarySave = () => {
    setShowDiaryEditor(false);
    setShowDiaryList(true);
    setSelectedDiaryEntry(null);
  };

  const handleDiaryEditorClose = () => {
    setShowDiaryEditor(false);
    setShowDiaryList(true);
    setSelectedDiaryEntry(null);
  };

  const handleDiaryListClose = () => {
    setShowDiaryList(false);
    setSelectedDiaryEntry(null);
  };

  const handleDateChange = async (date: string | null) => {
    setSelectedDate(date);
    if (date) {
      // Filter messages by selected date
      await filterMessagesByDate(date);
      // Scroll to bottom after filtering
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Reload all messages when filter is cleared
      await loadMessages();
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const headerUser = otherUser ?? (selectedFriend
    ? { id: selectedFriend.id, name: selectedFriend.name, isOnline: selectedFriend.isOnline, lastSeen: selectedFriend.lastSeenIst || '' }
    : null);

  if (!selectedFriend) {
    return (
      <FriendList
        friends={friends}
        onSelect={onSelectFriend}
        onRefresh={onFriendsRefresh}
        onLogout={onLogout}
      />
    );
  }

  if (!headerUser) {
    return <div className="flex items-center justify-center h-full bg-[#09090B] text-[#A1A1AA]">Loading...</div>;
  }

  return (
    <div className="h-full bg-[#09090B] bg-[radial-gradient(circle_at_18%_18%,rgba(124,58,237,0.16),transparent_28%),radial-gradient(circle_at_84%_78%,rgba(59,130,246,0.13),transparent_30%)] flex flex-col text-white">
      {selectedImageModal && (
        <ImageModal imageUrl={selectedImageModal} onClose={() => setSelectedImageModal(null)} />
      )}

      {showGallery && (
        <GalleryNew 
          userA={currentUserId} 
          userB={otherUserId ?? ''} 
          currentUserId={currentUserId}
          onDeleteMessage={deleteMessage}
          onClose={() => setShowGallery(false)} 
        />
      )}

      {showDiaryList && (
        <DiaryList
          userId={parseInt(currentUserId)}
          onClose={handleDiaryListClose}
          onSelectEntry={handleDiarySelect}
          onNewEntry={handleNewDiaryEntry}
        />
      )}

      {showDiaryEditor && (
        <DiaryEditor
          userId={parseInt(currentUserId)}
          entry={selectedDiaryEntry}
          onClose={handleDiaryEditorClose}
          onSave={handleDiarySave}
        />
      )}

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInviteSent={() => {
          onFriendsRefresh();
        }}
      />
      
      {/* Call Modal - unified for both audio and video calls */}
      {(inCall || inVideoCall || callStatus === 'Incoming call...' || callStatus === 'Calling...') && (
        <VideoCallModal
          isOpen={true}
          isIncoming={isIncomingCall && callStatus === 'Incoming call...'}
          callStatus={callStatus || ''}
          callTimer={callTimer}
          otherUserName={headerUser?.name || 'User'}
          localVideoRef={localVideoRef}
          remoteVideoRef={remoteVideoRef}
          localStream={localVideoStreamRef.current}
          onEndCall={endCall}
          onAccept={acceptCall}
          onReject={rejectCall}
          onToggleMute={toggleMute}
          onToggleVideo={toggleVideo}
          onSwitchCamera={switchCamera}
          isMuted={isMuted}
          isVideoOff={isVideoOff}
          callType={callType}
          cameraFacingMode={cameraFacingMode}
        />
      )}
      
      {/* Audio element for audio-only calls */}
      {!inVideoCall && (
        <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      )}
      
      <div className="flex-shrink-0">
        <ChatHeader 
          otherUser={headerUser} 
          onVideoCall={handleVideoCall}
          onAudioCall={handleAudioCall}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          isTyping={isTyping}
          isRecording={isOtherUserRecording}
          onBackToFriends={() => onSelectFriend(null)}
        />
      </div>
      
      <div className="flex-1 overflow-hidden">
        <MessageList
          messages={messages}
          currentUserId={currentUserId}
          hasMore={hasMore}
          loadingMore={loadingMore}
          messagesEndRef={messagesEndRef}
          onLoadMore={loadMoreMessages}
          onImageClick={setSelectedImageModal}
          onAddReaction={addReaction}
          onRemoveReaction={removeReaction}
          onDeleteMessage={deleteMessage}
          onReplyToMessage={handleReplyToMessage}
          otherUserName={headerUser?.name}
        />
      </div>
      
      <div className="flex-shrink-0">
        <ChatInput
          inputMessage={inputMessage}
          onMessageChange={setInputMessage}
          selectedImage={selectedImage}
          imagePreview={imagePreview}
          uploading={uploading}
          compressing={compressing}
          onSendMessage={handleSendMessage}
          onImageSelect={handleImageSelect}
          onImageUpload={handleImageUpload}
          onCancelImage={cancelImageUpload}
          isRecording={isRecording}
          recordingTime={recordingTime}
          audioBlob={audioBlob}
          audioUrl={audioUrl}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          onCancelRecording={cancelRecording}
          onUploadVoice={handleVoiceUpload}
          formatTime={formatTime}
          replyingTo={replyingTo}
          onCancelReply={handleCancelReply}
          otherUserName={headerUser?.name}
          currentUserId={currentUserId}
        />
      </div>
    </div>
  );
};

export default ChatWindow;
